import { useForm, zodResolver } from '@mantine/form'
import { useEffect } from 'react'
import { z } from 'zod'

import { useTranslation } from '@flecto/translations'
import { type CoreStore } from '@flecto/types'
import { isValidDomain } from '@flecto/utils'

export type UseFormStoreSettingsOptions = {
  store?: Partial<CoreStore>
}

export function useFormStoreSettings({ store }: UseFormStoreSettingsOptions) {
  const { t } = useTranslation('shared', { keyPrefix: 'validation' })

  const validateStringPlain = z.string({
    required_error: t('required'),
    invalid_type_error: t('required'),
  })

  const validateString = validateStringPlain
    .min(2, { message: t('required') })
    .trim()
    .refine((val) => val.trim().length > 0, { message: t('required') })

  const validateUrl = z.string().refine((value) => isValidDomain(value), {
    message: t('invalid_url'),
  })

  const validateDomain = z
    .string()
    .refine((value) => (value ? isValidDomain(value) : true), {
      message: t('invalid_url'),
    })

  const schema = z.object({
    domain: validateStringPlain
      .regex(/^(?![-_])[a-z0-9_-]+(?<![-_])$/, {
        message: t('invalid_identifier'),
      })
      .refine((val) => val.trim().length > 0, { message: t('required') }),
    apex_domain: validateDomain.nullable().optional(),
    name: validateString,
    slogan: validateString,
    phone_number: validateStringPlain.nullable(),
    phone_number_country_code: z.string().nullable(),
    email: z
      .string()
      .email({ message: t('invalid_email') })
      .nullable(),
    terms_and_conditions: validateStringPlain.nullable(),
    social_facebook_url: validateUrl
      .refine((val) => val.trim().includes('facebook'), {
        message: t('invalid_url'),
      })
      .nullable(),
    social_google_url: validateUrl
      .refine((val) => val.trim().includes('google'), {
        message: t('invalid_url'),
      })
      .nullable(),
    social_twitter_url: validateUrl
      .refine((val) => val.trim().includes('x.com'), {
        message: t('invalid_url'),
      })
      .nullable(),
    social_instagram_url: validateUrl
      .refine((val) => val.trim().includes('instagram'), {
        message: t('invalid_url'),
      })
      .nullable(),
    social_linkedin_url: validateUrl
      .refine((val) => val.trim().includes('linkedin'), {
        message: t('invalid_url'),
      })
      .nullable(),
    style_main_color: validateString.nullable(),
    style_font_family: validateStringPlain.nullable(),
    show_prices: z.boolean(),
    logo_image_url: validateStringPlain.nullable(),
    bg_image_url: validateStringPlain.nullable(),
    enabled: z.boolean(),
    show_prices_with_tax: z.boolean(),
  })

  const initialValues: Partial<CoreStore> = {
    domain: '',
    apex_domain: null,
    name: null,
    email: null,
    slogan: null,
    terms_and_conditions: null,
    social_facebook_url: null,
    social_google_url: null,
    social_twitter_url: null,
    social_instagram_url: null,
    social_linkedin_url: null,
    style_main_color: 'green',
    style_font_family: '',
    show_prices: false,
    logo_image_url: null,
    bg_image_url: null,
    phone_number: null,
    phone_number_country_code: null,
    enabled: false,
    show_prices_with_tax: false,
    ...store,
  }

  const form = useForm({
    initialValues,
    validate: zodResolver(schema),
    validateInputOnBlur: true,
    validateInputOnChange: true,
  })

  useEffect(() => {
    if (store) {
      form.setValues(store)
    }
  }, [store])

  return form
}
